// SOSModal — the two-question form. Deliberately minimal so an agent
// in the middle of a broken shift can file in under 5 seconds:
//
//   1. What broke? — 6 giant tap-target buttons.
//   2. One-line detail (optional).
//   3. Submit — fires POST /api/incidents/sos with auto-attached context.
//
// Auto-attached context (never asked from the agent):
//   * pageUrl — current window.location.href
//   * activeCallSid, activePDSession — pulled from window globals set by
//     softphone / power dial code
//   * consoleErrors — last N entries from the console ring buffer

import React, { useState } from 'react';
import { readConsoleErrorRing } from '../lib/consoleErrorRing.js';
import { apiPost } from '../lib/api.js';

const CLASSES = [
  { key: 'dialer', label: 'Dialer / Power Dial', hint: 'Calls not connecting, no ring, hangs up' },
  { key: 'sms', label: 'Texting / SMS', hint: 'Messages not sending or receiving' },
  { key: 'dispositions', label: 'Dispositions', hint: 'Post-call outcome won\u2019t save' },
  { key: 'login', label: 'Login', hint: 'Can\u2019t log in or keeps kicking me out' },
  { key: 'ui', label: 'App is broken', hint: 'Blank screen, crash, page won\u2019t load' },
  { key: 'other', label: 'Other', hint: 'Something else is stopping me from working' },
];

function readActiveCallSid() {
  try { return window.__CIVIX_ACTIVE_CALL_SID || null; } catch { return null; }
}
function readActivePDSession() {
  try { return window.__CIVIX_ACTIVE_PD_SESSION || null; } catch { return null; }
}

export default function SOSModal({ onClose }) {
  const [breakageClass, setBreakageClass] = useState(null);
  const [detail, setDetail] = useState('');
  const [state, setState] = useState('picking'); // picking | submitting | done | error
  const [errMsg, setErrMsg] = useState('');
  const [reporterCount, setReporterCount] = useState(0);
  const [joined, setJoined] = useState(false);

  async function submit() {
    if (!breakageClass) return;
    setState('submitting');
    setErrMsg('');
    try {
      // Use the shared apiPost helper so the auth token is attached from the
      // same localStorage key ('civix_token') the rest of the app uses.
      // Original implementation read localStorage.getItem('token') which is
      // NEVER set anywhere — the SOS request went out unauthenticated and
      // the server rejected it with 'Missing token'. Owner-reported via Ryan
      // 2026-08-14. apiPost throws a rich error on non-2xx which we surface
      // through the same errMsg path so the modal still shows a friendly
      // "Couldn't send" line if the server rejects.
      const body = {
        breakageClass,
        detail: detail.trim(),
        pageUrl: window.location.href,
        activeCallSid: readActiveCallSid(),
        activePDSession: readActivePDSession(),
        consoleErrors: readConsoleErrorRing(),
      };
      const data = await apiPost('/incidents/sos', body);
      if (!data?.ok) {
        setErrMsg(data?.error || 'submit failed');
        setState('error');
        return;
      }
      setReporterCount(data.reporterCount || 1);
      setJoined(!!data.joined);
      setState('done');
    } catch (err) {
      setErrMsg(err.message || 'Network error');
      setState('error');
    }
  }

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60" role="dialog" aria-modal="true" aria-label="System-down SOS">
      <div className="bg-[#0b0b1a] border border-[#DC2626] rounded-xl shadow-2xl w-full max-w-lg mx-4 p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[#DC2626] text-xs uppercase font-bold tracking-widest">SOS</div>
            <div className="text-white text-lg font-semibold">System is broken — you can't work</div>
            <div className="text-white/60 text-xs mt-1">This pages the CEO immediately. Only use if you actually can't work.</div>
          </div>
          <button type="button" aria-label="Close" className="text-white/60 hover:text-white" onClick={onClose}>×</button>
        </div>

        {state === 'done' ? (
          <div className="text-white space-y-3">
            <div className="text-green-400 font-semibold">SOS submitted.</div>
            <div className="text-white/80 text-sm">
              {joined
                ? `Your report joined an existing incident (${reporterCount} agents reporting the same thing). We already know.`
                : `You\u2019re the first to report this. The CEO has been paged.`}
            </div>
            <button type="button" onClick={onClose} className="mt-2 bg-white/10 hover:bg-white/20 text-white rounded-sm px-4 py-2">Close</button>
          </div>
        ) : (
          <>
            <div className="text-white text-sm mb-2 font-medium">1. What broke?</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {CLASSES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`sos-class-${c.key}`}
                  onClick={() => setBreakageClass(c.key)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    breakageClass === c.key
                      ? 'border-[#DC2626] bg-[#DC2626]/10'
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                >
                  <div className="text-white font-medium text-sm">{c.label}</div>
                  <div className="text-white/60 text-xs mt-0.5">{c.hint}</div>
                </button>
              ))}
            </div>

            <div className="text-white text-sm mb-2 font-medium">2. Anything else? (optional)</div>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, 500))}
              placeholder="What were you doing when it broke?"
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-md p-2 text-white text-sm mb-3 focus:outline-hidden focus:border-[#DC2626]"
              data-testid="sos-detail"
            />

            {state === 'error' ? (
              <div className="text-red-400 text-sm mb-3">Couldn't send: {errMsg}. Try again or refresh.</div>
            ) : null}

            <div className="flex items-center justify-between">
              <button type="button" onClick={onClose} className="text-white/60 hover:text-white text-sm">Cancel</button>
              <button
                type="button"
                data-testid="sos-submit"
                disabled={!breakageClass || state === 'submitting'}
                onClick={submit}
                className={`px-5 py-2 rounded-md font-semibold ${
                  breakageClass && state !== 'submitting'
                    ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white'
                    : 'bg-white/10 text-white/40 cursor-not-allowed'
                }`}
              >
                {state === 'submitting' ? 'Sending…' : 'Send SOS'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
